// Leave-periods tabs: a set of tabs that all swap one shared turbo-frame panel
// (#tab-content) in place. Implements the full WCAG tabs pattern (CU-1248uf3wj1q /
// CU-1248uf3wvre): roving tabindex, Arrow/Home/End navigation, aria-selected, and the
// panel's aria-labelledby.
//
// MANUAL activation (per the WAI-ARIA APG): because selecting a tab loads the panel over
// the network, arrow keys only MOVE focus — they do not load. The focused tab is activated
// (panel loaded, aria-selected set) on Enter, Space or click, so arrowing across the strip
// does not fire a request for every tab passed over.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab"]

  connect() {
    const anchor = window.location.hash.slice(1)
    const tab = this.tabFor(anchor) || this.activeTab() || this.tabTargets[0]
    // Reconcile the selected tab and load its panel without stealing focus on load.
    this.activate(tab, { focus: false })
  }

  // Click on a tab selects it.
  switchTab(event) {
    event.preventDefault()
    this.activate(event.currentTarget, { focus: true })
  }

  keydown(event) {
    const tabs = this.tabTargets
    const current = tabs.indexOf(event.currentTarget)

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        this.focusTab(tabs[(current + 1) % tabs.length])
        break
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        this.focusTab(tabs[(current - 1 + tabs.length) % tabs.length])
        break
      case "Home":
        event.preventDefault()
        this.focusTab(tabs[0])
        break
      case "End":
        event.preventDefault()
        this.focusTab(tabs[tabs.length - 1])
        break
      case " ":
        // Space activates the focused tab (links do this on Enter natively, not Space).
        event.preventDefault()
        this.activate(event.currentTarget, { focus: true })
        break
      default:
    }
  }

  // Move focus and the roving tabindex only; selection (aria-selected / panel) is unchanged.
  focusTab(tab) {
    this.tabTargets.forEach((t) => t.setAttribute("tabindex", t === tab ? "0" : "-1"))
    tab.focus()
  }

  // Select a tab: set active/aria-selected/roving tabindex, label and load the shared panel.
  activate(tab, { focus }) {
    if (!tab) return

    this.tabTargets.forEach((t) => {
      const selected = t === tab
      t.classList.toggle("active", selected)
      t.setAttribute("aria-selected", selected ? "true" : "false")
      t.setAttribute("tabindex", selected ? "0" : "-1")
    })

    const state = tab.dataset.tabsStateParam
    const frame = document.getElementById("tab-content")
    if (frame) {
      frame.setAttribute("aria-labelledby", tab.id)
      frame.src = `/admin/leave_periods/list?state=${state}`
    }

    history.replaceState(null, null, `#${state}`)
    if (focus) tab.focus()
  }

  activeTab() {
    return this.tabTargets.find((t) => t.classList.contains("active"))
  }

  tabFor(state) {
    if (!state) return null
    return this.tabTargets.find((t) => t.dataset.tabsStateParam === state)
  }
}
